import { OperacaoJourneyFunnel } from '@/components/Dashboard/OperacaoJourneyFunnel'
import { OperacaoKpiGrid } from '@/components/Dashboard/OperacaoKpiGrid'
import { OperacaoLatestDeals } from '@/components/Dashboard/OperacaoLatestDeals'
import { OperacaoPendenciasSection } from '@/components/Dashboard/OperacaoPendenciasSection'
import { OperacaoPipelineDonut } from '@/components/Dashboard/OperacaoPipelineDonut'
import { OperacaoRecentActivity } from '@/components/Dashboard/OperacaoRecentActivity'
import { OperacaoTrendChart } from '@/components/Dashboard/OperacaoTrendChart'
import {
  getOperacaoSideChips,
  groupDealsByJourneyMacro,
  type OperacaoSummaryView,
} from '@/components/Dashboard/operacaoTab.utils'
import { useOperacaoPendencias } from '@/hooks/useOperacaoPendencias'
import type { DashboardLatestDeals, DashboardRecentActivity } from '@/hooks/dashboardDataTypes'

interface MonthlyPoint {
  month: string
  count: number
  revenue: number
}

type OperacaoTabProps = {
  summary: OperacaoSummaryView
  monthlyTrend?: MonthlyPoint[]
  latestDeals: DashboardLatestDeals[]
  recentActivity: DashboardRecentActivity[]
  valuesVisible?: boolean
}

export function OperacaoTab({
  summary,
  monthlyTrend = [],
  latestDeals,
  recentActivity,
  valuesVisible = false,
}: OperacaoTabProps) {
  const pendencias = useOperacaoPendencias()
  const journeySteps = groupDealsByJourneyMacro(summary.dealsByStage)
  const sideChips = getOperacaoSideChips(summary.dealsByStage)

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-1'>
        <p className='text-sm text-text-muted'>
          Resumo da operação comercial — pipeline, pendências e movimentações do período.
        </p>
      </div>

      <OperacaoPendenciasSection
        items={pendencias.items}
        totalCount={pendencias.totalCount}
        loading={pendencias.loading}
      />

      <OperacaoKpiGrid
        summary={summary}
        monthlyTrend={monthlyTrend}
        valuesVisible={valuesVisible}
      />

      <div className='grid grid-cols-1 gap-4 xl:grid-cols-3'>
        <div className='xl:col-span-2'>
          <OperacaoTrendChart
            createdByMonth={summary.dealsCreatedByMonth}
            closedByMonth={monthlyTrend.map((row) => ({
              month: row.month,
              count: row.count,
              revenue: row.revenue,
            }))}
            pipelineCount={summary.pipelineCount}
            valuesVisible={valuesVisible}
          />
        </div>
        <OperacaoPipelineDonut
          steps={journeySteps}
          pipelineCount={summary.pipelineCount}
          valuesVisible={valuesVisible}
        />
      </div>

      <OperacaoJourneyFunnel
        steps={journeySteps}
        pipelineCount={summary.pipelineCount}
        pipelineValue={summary.pipelineValue}
        valuesVisible={valuesVisible}
      />

      {sideChips.length > 0 ? (
        <div className='flex flex-wrap gap-2'>
          {sideChips.map((chip) => (
            <span
              key={chip.label}
              className='inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--bg-card)] px-3 py-1 text-xs font-medium text-text-secondary shadow-sm'
            >
              <span className='h-2 w-2 rounded-full' style={{ backgroundColor: chip.color }} />
              {chip.label}: {chip.count}
            </span>
          ))}
        </div>
      ) : null}

      <OperacaoLatestDeals deals={latestDeals} valuesVisible={valuesVisible} />

      <OperacaoRecentActivity activities={recentActivity} />
    </div>
  )
}
